import { useEffect, useRef, useState } from 'react';
import { IMAGE_PRO_URL } from '../../../lib/endpoints/promotion';
import { TagPromotionModel } from '../../../models/tagPromotion.model';
import { PromotionEnumType } from '../../../enums/promotionEnum';
import { generateImageFileNameWithRandomName } from '../../../utils/generateImageFileName';
import {
  createTagPromotion,
  deleteTagPromotion,
  updateTagPromotion,
} from '../../../services/tagPromotion';
import { ColorTagPromotionEnum } from '../../../enums/colorTagPromotion';

interface Props {
  data: TagPromotionModel[];
  onFetchTagPromotionData: VoidFunction;
}

const useTagPromotionController = ({ data, onFetchTagPromotionData }: Props) => {
  const [selectedData, setSelectedData] = useState<TagPromotionModel>(null!);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [promotionType, setPromotionType] = useState<PromotionEnumType>(
    PromotionEnumType.buy_x_get_x
  );

  const [selectedColor, setSelectedColor] = useState<ColorTagPromotionEnum>(
    ColorTagPromotionEnum.ORANGE
  );
  const [imageToShow, setImageToShow] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openDeleteForm, setOpenDeleteForm] = useState<boolean>(false);
  const [openPopover, setOpenPopover] = useState(null);   

  const TagPromotionNameRef = useRef<HTMLInputElement>(null!);

  const [currentPage, setCurrentPage] = useState<number>(1);

  const handleValidate = () => {
    if (!selectedData && !imageFile) {
      setErrorMessage('Please select an image.');
      return false;
    }
    if (!TagPromotionNameRef.current?.value) {
      setErrorMessage('Please input Tag Promotion Name.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleCreateTagPromotion = async (): Promise<void> => {
    try {
      setLoadingButton(true);
      await createTagPromotion({
        tagType: promotionType,
        tagName: TagPromotionNameRef.current.value,
        color: selectedColor,
        image: generateImageFileNameWithRandomName(imageFile),
      });
      setOpenConfirm(false);
      await onFetchTagPromotionData();
      TagPromotionNameRef.current.value = '';
      setImageToShow(null);
      setSelectedData(null!);
    } catch (error) {
      console.error('Error creating tag promotion:', error);
    } finally {
      setLoadingButton(false);
      setLoading(false);
    }
  };

  const handleUpdateTagPromotion = async (): Promise<void> => {
    try {
      setLoadingButton(true);
      await updateTagPromotion({
        id: selectedId,
        tagType: promotionType,
        tagName: TagPromotionNameRef.current.value,
        color: selectedColor,
        image: generateImageFileNameWithRandomName(imageFile),
      });
      setOpenConfirm(false);
      await onFetchTagPromotionData();
      TagPromotionNameRef.current.value = '';
      setImageToShow(null);
      setSelectedData(null!);
    } catch (error) {
      console.error('Error creating tag promotion:', error);
    } finally {
      setLoadingButton(false);
      setLoading(false);
    }
  };

  const handleSubmitForm = () => {
    if (selectedData) {
      handleUpdateTagPromotion();
    } else {
      handleCreateTagPromotion();
    }
  };

  const handleDeleteTagPromotion = async (): Promise<void> => {
    try {
      setLoadingButton(true);
      if (selectedId) {
        await deleteTagPromotion(selectedId);
      }
      setOpenDeleteForm(false);
      await onFetchTagPromotionData();
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingButton(false);
    }
  };

  const handleClickEditButton = () => {
    const getDataByFindId = data?.find((item: TagPromotionModel) => item?.id === selectedId);
    if (getDataByFindId) {
      setSelectedData(getDataByFindId);
    }
    setOpenPopover(null);
  };

  const handleCancelEditTagPro = () => {
    TagPromotionNameRef.current.value = '';
    setImageToShow(null);
    setSelectedData(null!);
    setSelectedId('');
  };

  const handleOpenDeleteForm = () => {
    const getDataByFindId = data?.find((item: TagPromotionModel) => item?.id === selectedId);
    if (getDataByFindId) {
      setSelectedId(getDataByFindId?.id);
      setOpenDeleteForm(true);
      setOpenPopover(null);
    }
  };

  const handleCloseDeleteForm = () => {
    setOpenDeleteForm(false);
  };

  const handleOpenConfirmForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (handleValidate()) {
      setOpenConfirm(true);
    }
  };

  const handleSelectedImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2 && typeof reader.result === 'string') {
          setImageToShow(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenPopover = (e: any, itemId: string) => {
    setOpenPopover(e.currentTarget);
    setSelectedId(itemId);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleSelectedColor = (color: ColorTagPromotionEnum) => {
    setSelectedColor(color);
  };

  const filterData = data;
  const rows = filterData.slice((currentPage - 1) * 10, currentPage * 10);
  const countPagination = Math.ceil(filterData?.length / 10);

  useEffect(() => {
    if (selectedData) {
      setPromotionType(selectedData?.tagType as PromotionEnumType);
      TagPromotionNameRef.current.value = selectedData?.tagName;
      setSelectedColor(selectedData?.color as ColorTagPromotionEnum)
      setImageToShow(IMAGE_PRO_URL + selectedData?.image?.url);
    }
  }, [selectedData]);

  return {
    imageToShow,
    handleSelectedImage,
    handleChangePage: (_: any, newPage: any) => setCurrentPage(newPage),
    currentPage,
    loading,
    rows,
    countPagination,
    promotionType,
    handleSelectPromotionType: (e: any) => setPromotionType(e.target.value),
    handleSubmitForm,
    TagPromotionNameRef,
    handleOpenConfirmForm,
    openConfirm,
    errorMessage,
    loadingButton,
    handleCloseForm: () => setOpenConfirm(false),
    handleClosePopover,
    handleOpenPopover,
    openPopover,
    handleOpenDeleteForm,
    handleDeleteTagPromotion,
    openDeleteForm,
    handleCloseDeleteForm,
    handleClickEditButton,
    handleCancelEditTagPro,
    selectedData,
    handleSelectedColor,
    selectedColor,
  };
};
export default useTagPromotionController;
